import React, {useReducer, useState, useEffect} from 'react';
import {useSelector} from 'react-redux';
import {Subheading} from 'app/styles/heading';
import {Content} from 'app/styles/content';
import styled from 'styled-components';
import {TITLE_POSTFIX} from 'app/seo/seo.constants';
import {Helmet} from 'react-helmet-async';
import AgeGroupRow from 'app/components/competitions/AgeGroupRow';
import {SIZES, LoadingIndicator, LOADING_MESSAGE} from '@fupa/fupa-uikit';
import {
  fetchingCompetitionDefaultAgeGroups,
  fetchingCompetitionDefaultCompetitionsByAgeGroup,
  updateOpenAgeGroup,
} from 'app/routes/competition/CompetitionPageActions';
import {createSelector} from 'reselect';
import {useFetchData, useRestoreData, useEffectAfterMount} from 'app/hooks/dataHooks';
import {CompetitionPageReducer, initData} from 'app/routes/competition/CompetitionPageReducer';
import {useLocation, useParams} from 'react-router';
import {LastCompetitions} from 'app/components/competitions/LastCompetitions';

const Container = styled.div`
  padding-bottom: ${SIZES['24']};
`;

const CompetitionSubheading = styled(Subheading)`
  &&& {
    margin-bottom: 0;
    line-height: ${SIZES['24']};
  }
`;

const selectDistrict = createSelector(
  state => state.session.district,
  district => {
    return {
      districtName: district.name,
    };
  }
);

const CompetitionDefaultPage = ({dispatchRedux}) => {
  const {districtName} = useSelector(selectDistrict);
  const {restoredData} = useRestoreData('CompetitionDefaultPage');
  const initialState = restoredData ?? initData;
  const {params, districtSlug} = useParams();
  const [competitionCategory, _, ageGroupSlug] = params ? params.split('/').filter(item => item) : [];
  const activeKey = competitionCategory && ageGroupSlug ? `${competitionCategory}_${ageGroupSlug}` : null;
  const {pathname} = useLocation();

  const [state, dispatch] = useReducer(CompetitionPageReducer, initialState);
  const {ageGroups, competitions, openAgeGroup} = state;
  const {items, isFetching} = ageGroups;
  const [initRender, setInitRender] = useState(true);
  const [mount, setMount] = useState(true);

  useEffect(() => {
    // Detect if age group is opened by page load and not by user interaction
    if (mount) {
      setMount(false);
      return;
    }
    if (initRender) {
      setInitRender(false);
    }
  }, [pathname, openAgeGroup]);

  const loadData = () => {
    return Promise.all([
      fetchingCompetitionDefaultAgeGroups(districtSlug, dispatchRedux, activeKey)(dispatch, state),
      fetchingCompetitionDefaultCompetitionsByAgeGroup(
        ageGroupSlug,
        competitionCategory,
        districtSlug
      )(dispatch, state),
    ]);
  };
  const setOpenAgeGroup = key => {
    dispatch(updateOpenAgeGroup(key));
  };
  useFetchData(state, loadData, 'CompetitionDefaultPage');

  useEffectAfterMount(() => loadData(), [params]);
  const title = `Ligen, Pokalwettbewerbe und Turniere${TITLE_POSTFIX} ${districtName}${TITLE_POSTFIX}`;
  const meta = [
    {
      name: 'robots',
      content: 'index, follow',
    },
    {
      name: 'description',
      content: `Alle aktuellen Ligen, Pokalwettbewerbe und Turniere aus der Region ${districtName}.`,
    },
  ];

  const getAgeGroupsByCompetitionType = competitionType => {
    return competitionType.ageGroups.map(ageGroup => {
      const objectKey = competitionType.category.id + '_' + ageGroup.slug;
      const competitionState = ageGroup?.competitionSeasons ? {[objectKey]: ageGroup.competitionSeasons} : competitions;
      return (
        <AgeGroupRow
          key={objectKey}
          competitionsState={competitionState}
          ageGroup={ageGroup}
          urlWithoutSubPath={Boolean(ageGroup?.competitionSeasons)}
          districtSlug={districtSlug}
          districtName={districtName}
          competitionCategoryAgeGroup={competitionType.category}
          openAgeGroup={openAgeGroup}
          initRender={initRender}
          setOpenAgeGroup={setOpenAgeGroup}
        />
      );
    });
  };

  const competitionTypes = items.length ? (
    items.map(item => {
      const ageGroupsByCompetitionType = getAgeGroupsByCompetitionType(item);
      return ageGroupsByCompetitionType.length > 0 ? (
        <div key={item.category.name}>
          <CompetitionSubheading variant='subheading2'>{item.category.name}</CompetitionSubheading>
          {ageGroupsByCompetitionType}
        </div>
      ) : null;
    })
  ) : isFetching ? (
    <LoadingIndicator message={LOADING_MESSAGE.loadingData} bgColor={false} />
  ) : null;

  return (
    <Content>
      <Helmet title={title} meta={meta} />
      <Container>
        <LastCompetitions />
        {competitionTypes}
      </Container>
    </Content>
  );
};

export default CompetitionDefaultPage;
